import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ShieldAlert, ChevronRight, CheckCircle2 } from 'lucide-react';
import { timeAgo } from '../utils/relativeTime';
import ClearanceBadge from './ClearanceBadge';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * "Injured This Week" — lightweight compact table for the dashboard.
 * Source: GET /api/injury-recovery/active-injuries (Admin + Staff only).
 * Inline action: advance current recovery-plan phase via the EXISTING
 * PUT /api/injury-recovery/recovery-plans/{plan_id}/phase/{phase_index}/complete.
 * No new endpoints, no new data.
 */
const InjuredThisWeek = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('week'); // week | all

  const load = async () => {
    const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    try {
      const r = await axios.get(`${API}/api/injury-recovery/active-injuries`, h);
      setRows(r.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const visible = (filter === 'week'
    ? rows.filter((r) => {
        const d = new Date(r.date_of_injury || r.created_at || 0).getTime();
        return !Number.isNaN(d) && d >= weekAgo;
      })
    : rows
  ).slice(0, 10);

  const advancePhase = async (row) => {
    const plan = row?.recovery_plan;
    if (!plan?.id) {
      toast.error('No recovery plan — open record to start one.');
      return;
    }
    const phaseIndex = plan.current_phase || 1;
    setBusyId(row.injury_id || row.id);
    try {
      const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      await axios.put(
        `${API}/api/injury-recovery/recovery-plans/${plan.id}/phase/${phaseIndex}/complete`,
        {},
        h
      );
      toast.success('Phase marked complete.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to advance phase');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700" data-testid="injured-this-week">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <ShieldAlert size={14} className="text-red-500" />
            Injured This Week
            <span className="text-[11px] font-normal text-gray-500 dark:text-gray-400">({visible.length})</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-md bg-gray-100 dark:bg-gray-700 p-0.5 text-[10px]">
              <button
                className={`px-2 py-0.5 rounded-sm ${filter === 'week' ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-gray-100' : 'text-gray-500'}`}
                onClick={() => setFilter('week')}
                data-testid="injured-filter-week"
              >
                Week
              </button>
              <button
                className={`px-2 py-0.5 rounded-sm ${filter === 'all' ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-gray-100' : 'text-gray-500'}`}
                onClick={() => setFilter('all')}
                data-testid="injured-filter-all"
              >
                All
              </button>
            </div>
            <button
              onClick={() => navigate('/injury-recovery')}
              className="text-[11px] text-pfap-green hover:underline flex items-center"
              data-testid="injured-open"
            >
              Open <ChevronRight size={12} />
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <p className="text-xs text-gray-400 text-center py-6">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-6" data-testid="injured-empty">
            No injuries {filter === 'week' ? 'in the last 7 days' : 'currently'}.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {visible.map((r) => {
              const injuryId = r.injury_id || r.id;
              const plan = r.recovery_plan;
              // Backend returns the athlete as a nested object: { id, full_name, squad, ... }
              // When the referenced athlete has been deleted, the backend still returns
              // { id: athlete_id } without a full_name, so we treat that as "Unlinked".
              const athleteId = r.athlete?.id;
              const athleteName = r.athlete?.full_name;
              const athleteSquad = r.athlete?.squad;
              const hasAthlete = Boolean(athleteId && athleteName);
              const openAthlete = () => {
                if (!hasAthlete) {
                  navigate('/injury-recovery');
                  return;
                }
                navigate(`/athlete/${athleteId}/360`);
              };
              return (
                <li
                  key={injuryId}
                  className="py-2 flex items-center justify-between gap-3"
                  data-testid={`injured-row-${injuryId}`}
                >
                  <div
                    className={`min-w-0 flex-1 ${hasAthlete ? 'cursor-pointer' : 'cursor-default'}`}
                    onClick={openAthlete}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') openAthlete(); }}
                    data-testid={`injured-link-${injuryId}`}
                  >
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {hasAthlete ? (
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {athleteName}
                        </p>
                      ) : (
                        <p
                          className="text-sm font-medium text-gray-500 dark:text-gray-400 italic truncate"
                          data-testid={`injured-unlinked-${injuryId}`}
                          title="Athlete record missing or deleted — click row to open Injury & Recovery"
                        >
                          Unlinked athlete
                        </p>
                      )}
                      {athleteSquad ? (
                        <Badge className="text-[9px] bg-gray-100 dark:bg-gray-700 text-gray-500">{athleteSquad}</Badge>
                      ) : null}
                      <ClearanceBadge value={r.status} testId={`injured-clearance-${injuryId}`} />
                    </div>
                    <div className="text-[10px] text-gray-500 mt-0.5 flex flex-wrap gap-x-2">
                      <span>{r.body_area || 'Injury'}</span>
                      <span>·</span>
                      <span>logged {timeAgo(r.date_of_injury)}</span>
                      {r.current_phase_name ? (
                        <>
                          <span>·</span>
                          <span>phase: <span className="text-gray-700 dark:text-gray-300">{r.current_phase_name}</span></span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  {plan?.id ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px] px-2 gap-1"
                      disabled={busyId === injuryId}
                      onClick={() => advancePhase(r)}
                      data-testid={`injured-advance-${injuryId}`}
                    >
                      <CheckCircle2 size={10} /> Advance phase
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-[11px] px-2"
                      onClick={() => navigate('/injury-recovery')}
                      data-testid={`injured-open-${injuryId}`}
                    >
                      Open
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default InjuredThisWeek;
